import * as THREE from 'three';

/**
 * フェルト（ラシャ）色テーマ（自前・GPL非依存）。
 *
 * bed クロス・クッション・ポケット喉部は同一のフェルト。色を一括管理し、設定UIから
 * `setFeltColor()` で全マテリアルへ反映する。クッションは bed より少し暗い同系色、
 * 喉部は bed と同色（現状の見た目を踏襲）。選択色は localStorage に永続化する。
 */

export type FeltRole = 'bed' | 'cushion' | 'throat';

const STORAGE_KEY = 'pool.feltColor';
const DEFAULT_FELT = 0x3a4047; // チャコール（デフォルト）

interface Registered {
  mat: THREE.MeshStandardMaterial;
  role: FeltRole;
}
const registered: Registered[] = [];
let feltColor = DEFAULT_FELT;

/** role ごとの色（cushion は bed の約 0.86 倍の明度＝現状の 0x196b36/0x1d7a3e 比）。 */
const colorFor = (role: FeltRole): THREE.Color => {
  const c = new THREE.Color(feltColor);
  if (role === 'cushion') c.multiplyScalar(0.86);
  return c;
};

const applyOne = (r: Registered): void => {
  r.mat.color.copy(colorFor(r.role));
};

/** フェルトマテリアルを登録し、現在色を即適用する。 */
export const registerFeltMaterial = (
  mat: THREE.MeshStandardMaterial,
  role: FeltRole,
): void => {
  registered.push({ mat, role });
  applyOne({ mat, role });
};

/** 現在のフェルト基準色（bed 相当）。 */
export const getFeltColor = (): number => feltColor;

/** フェルト色を一括変更し、登録済みマテリアルへ反映 + 永続化する。 */
export const setFeltColor = (hex: number): void => {
  feltColor = hex & 0xffffff;
  for (const r of registered) applyOne(r);
  try {
    localStorage.setItem(STORAGE_KEY, `#${feltColor.toString(16).padStart(6, '0')}`);
  } catch (err) {
    console.error('setFeltColor: localStorage 保存失敗:', err);
  }
};

/** localStorage から保存色を読む（無ければ既定色）。 */
export const loadFeltColor = (): number => {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    if (v) {
      const n = parseInt(v.replace('#', ''), 16);
      if (Number.isFinite(n)) return n & 0xffffff;
    }
  } catch (err) {
    console.error('loadFeltColor: localStorage 読込失敗:', err);
  }
  return DEFAULT_FELT;
};
